import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  actionNeedsReason,
  allowedActions,
  categoriesText,
  categoryLabel,
  DAMAGE_CATEGORIES,
  de,
  DESCRIPTION_MAX,
  hasAtLeastRole,
  mediaRequirementMet,
  statusLabel,
  targetStatus,
  TITLE_MAX,
  validateDamageTitle,
  viewLabel,
  type AircraftView,
  type DamageCategory,
  type DamageStatus,
  type MemberRole,
  type NormalizedPoint,
} from '@fsv/core';
import { useSettings, useWrite, useTenantPath, useDamageRef } from '../data/hooks';
import { PhotoUploadError, randomId } from '@fsv/data';
import type { Aircraft, Damage, NewImage, UpdateDamageInput, UploadProgress } from '@fsv/data';
import { tx } from '@fsv/data';
import { formatRelative } from '@fsv/data';
import { failureSummary, keepFailed } from '@fsv/data';
import { ChoiceChips, MultiChoiceChips } from './Controls';
import { Icon } from './Icon';
import { GroundedWarning } from './NoticePanel';
import { PhotoPicker } from './PhotoPicker';
import { Sheet } from './Sheet';
import { StatusBadge, StatusGlyph } from './Status';
import './DamageSheets.css';

const CREATE_STATUSES: DamageStatus[] = ['bekannt', 'beobachtung', 'in_reparatur', 'behoben'];

function categoryOptions() {
  return DAMAGE_CATEGORIES.map((c) => ({ value: c, label: categoryLabel[c] }));
}

function statusOptions(statuses: DamageStatus[]) {
  return statuses.map((s) => ({
    value: s,
    className: `st--${s}`,
    label: (
      <>
        <StatusGlyph status={s} size={12} />
        {statusLabel[s]}
      </>
    ),
  }));
}

/** Title + description with the same limits and messages as the API. */
function TextFields({
  title,
  description,
  onTitle,
  onDescription,
  showErrors,
}: {
  title: string;
  description: string;
  onTitle: (v: string) => void;
  onDescription: (v: string) => void;
  showErrors: boolean;
}) {
  const id = useId();
  const titleError = showErrors ? validateDamageTitle(title) : null;
  return (
    <>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-t`}>
          {de.damage.title}
        </label>
        <input
          id={`${id}-t`}
          className="input"
          value={title}
          maxLength={TITLE_MAX}
          placeholder={tx.create.titlePlaceholder}
          onChange={(e) => onTitle(e.target.value)}
          aria-invalid={titleError !== null}
          aria-describedby={titleError ? `${id}-te` : undefined}
          enterKeyHint="next"
        />
        {titleError && (
          <p id={`${id}-te`} className="field__error">
            {titleError.message}
          </p>
        )}
      </div>
      <div className="field">
        <label className="field__label" htmlFor={`${id}-d`}>
          {de.damage.description}
        </label>
        <textarea
          id={`${id}-d`}
          className="textarea"
          value={description}
          maxLength={DESCRIPTION_MAX}
          placeholder={tx.create.descriptionPlaceholder}
          onChange={(e) => onDescription(e.target.value)}
        />
      </div>
    </>
  );
}

/** "Is this already known?" — shown BEFORE the form whenever something is nearby. */
function NearbyStep({
  nearby,
  isEditor,
  onSame,
  onNew,
  onLeave,
}: {
  nearby: Damage[];
  isEditor: boolean;
  onSame: (d: Damage) => void;
  onNew: () => void;
  onLeave: () => void;
}) {
  const ref = useDamageRef();
  const to = useTenantPath();
  return (
    <div className="nearby" data-tour="report-nearby">
      <div className="notice nearby__head" role="status">
        <Icon name="alert" />
        <div>
          <strong>{de.damage.nearbyWarning(nearby.length)}</strong>
          <p>{tx.create.nearbyIntro}</p>
        </div>
      </div>
      <ul className="nearby__list">
        {nearby.map((d) => (
          <li key={d.id} className={`nearby__item st--${d.status}`}>
            <div className="nearby__thumb">{d.thumbUrl ? <img src={d.thumbUrl} alt="" /> : <Icon name="image" />}</div>
            <div className="nearby__info">
              <div className="row">
                <span className="mono nearby__num">{ref(d.number)}</span>
                <StatusBadge status={d.status} />
              </div>
              <div className="nearby__title">{d.title}</div>
              <div className="muted nearby__meta">
                {categoriesText(d.categories)} · {formatRelative(d.reportedAt, new Date())}
              </div>
              <div className="row nearby__actions">
                <Link className="btn" to={to(`/schaden/${d.id}`)} onClick={onLeave}>
                  {tx.create.toExisting}
                  <Icon name="forward" size={18} />
                </Link>
                {isEditor && (
                  <button type="button" className="btn btn--ink" onClick={() => onSame(d)}>
                    <Icon name="merge" size={18} />
                    {de.damage.sameDamage}
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn--primary btn--block" onClick={onNew}>
        {tx.create.newAnyway}
        <Icon name="forward" />
      </button>
    </div>
  );
}

export function CreateDamageSheet({
  aircraft,
  view,
  point,
  nearby,
  role,
  onClose,
  onCreated,
}: {
  aircraft: Aircraft;
  view: AircraftView;
  point: NormalizedPoint;
  nearby: Damage[];
  role: MemberRole;
  onClose: () => void;
  onCreated: (damage: Damage) => void;
}) {
  const ref = useDamageRef();
  const write = useWrite();
  const settings = useSettings();
  const isEditor = hasAtLeastRole(role, 'editor');
  const [step, setStep] = useState<'nearby' | 'form' | 'same'>(nearby.length > 0 ? 'nearby' : 'form');
  const [same, setSame] = useState<Damage | null>(null);
  const [categories, setCategories] = useState<DamageCategory[]>([]);
  const [status, setStatus] = useState<DamageStatus>('bekannt');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState<NewImage[]>([]);
  const [photoErrors, setPhotoErrors] = useState<string[]>([]);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  // One id for this report, however often "Melden" is pressed after a network error (migration 0012).
  const [requestId] = useState(randomId);
  const changePhotos = (next: NewImage[]) => {
    setPhotos(next);
    setPhotoErrors([]);
  };

  // The club's photo rule (media.ts): said before the server would refuse it.
  const photoMissing = !mediaRequirementMet(role, settings.data?.mediaRequired, photos.length);
  const submit = async () => {
    setShowErrors(true);
    if (!categories.length || validateDamageTitle(title) || photoMissing) return;
    setSaving(true);
    const result = await write({
      run: (s) =>
        s.createDamage(
          { aircraftId: aircraft.id, view, x: point.x, y: point.y, categories, title, description: description.trim() || null, status: isEditor ? status : undefined, images: photos, clientRequestId: requestId },
          { onProgress: setProgress },
        ),
      success: (d, r) => {
        const text = d.status === 'gemeldet' ? tx.toast.reported(ref(d.number)) : tx.toast.created(ref(d.number));
        // The damage exists either way; refused photos are named so they can be added again from the detail page.
        return r.imageFailures.length ? `${text} ${failureSummary(r.imageFailures, photos.length)}` : text;
      },
      undo: (s, r) => s.deleteDamage(r.value.id, { undoOf: r.eventId }),
    });
    setSaving(false);
    setProgress(null);
    if (result) onCreated(result.value);
  };

  const submitSame = async () => {
    if (!same) return;
    setSaving(true);
    const total = photos.length;
    const result = await write({
      run: (s) => s.addImages(same.id, photos, { mergedReport: { note: note.trim() || null }, onProgress: setProgress }),
      success: (v, r) => (r.failures.length ? `${tx.toast.photosAdded(v.length)} ${failureSummary(r.failures, total)}` : tx.toast.photosAdded(v.length)),
      undo: async (s, r) => {
        for (const [i, img] of r.value.entries()) await s.removeImage(img.id, { undoOf: r.eventIds[i] ?? r.eventId });
      },
      onError: (e) => {
        if (!(e instanceof PhotoUploadError)) return;
        const kept = keepFailed(photos, e.failures);
        setPhotos(kept.photos);
        setPhotoErrors(kept.errors);
      },
    });
    setSaving(false);
    setProgress(null);
    if (!result) return;
    if (result.failures.length) {
      // Keep the sheet open with exactly the refused photos, each with its reason.
      const kept = keepFailed(photos, result.failures);
      setPhotos(kept.photos);
      setPhotoErrors(kept.errors);
      return;
    }
    onCreated(same);
  };

  const kicker = (
    <span className="label">
      <span className="mono">{aircraft.registration}</span> · {viewLabel[view]}
    </span>
  );

  if (step === 'nearby') {
    return (
      <Sheet title={tx.create.nearbyTitle} kicker={kicker} onClose={onClose} tour="report-sheet">
        <GroundedWarning aircraftId={aircraft.id} />
        <NearbyStep
          nearby={nearby}
          isEditor={isEditor}
          onLeave={onClose}
          onNew={() => setStep('form')}
          onSame={(d) => {
            setSame(d);
            setStep('same');
          }}
        />
      </Sheet>
    );
  }

  if (step === 'same' && same) {
    return (
      <Sheet
        title={tx.create.sameTitle(ref(same.number))}
        kicker={kicker}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setStep('nearby')}>
              {tx.app.back}
            </button>
            <button type="button" className="btn btn--primary" disabled={saving || photos.length === 0} onClick={submitSame}>
              <Icon name="merge" />
              {tx.create.sameSave}
            </button>
          </>
        }
      >
        <p>{tx.create.sameIntro}</p>
        <PhotoPicker photos={photos} onChange={changePhotos} existingCount={same.imageCount} errors={photoErrors} progress={progress} />
        <div className="field">
          <label className="field__label" htmlFor="same-note">
            {tx.create.sameNote}
          </label>
          <textarea id="same-note" className="textarea" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={isEditor ? tx.create.createTitle : tx.create.reportTitle}
      kicker={kicker}
      onClose={onClose}
      tour="report-sheet"
      footer={
        <>
          {nearby.length > 0 && (
            <button type="button" className="btn" onClick={() => setStep('nearby')}>
              {tx.app.back}
            </button>
          )}
          <button type="button" className="btn btn--primary" disabled={saving} onClick={submit} data-tour="report-submit">
            <Icon name={isEditor ? 'check' : 'upload'} />
            {isEditor ? tx.create.submitEditor : tx.create.submitMember}
          </button>
        </>
      }
    >
      <GroundedWarning aircraftId={aircraft.id} />
      <MultiChoiceChips
        label={de.damage.category}
        values={categories}
        options={categoryOptions()}
        onChange={setCategories}
        hint={tx.create.categoriesHint}
        error={showErrors && !categories.length ? tx.create.categoryRequired : null}
      />
      <TextFields title={title} description={description} onTitle={setTitle} onDescription={setDescription} showErrors={showErrors} />
      <div data-tour="report-photos">
        <PhotoPicker photos={photos} onChange={setPhotos} progress={progress} />
        {photoMissing && (
          <p className={showErrors ? 'field__error' : 'field__hint'} data-testid="photo-required">
            {de.media.required}
          </p>
        )}
      </div>
      {isEditor ? (
        <ChoiceChips label={de.damage.status} value={status} options={statusOptions(CREATE_STATUSES)} onChange={setStatus} />
      ) : (
        <div className="member-status">
          <StatusBadge status="gemeldet" />
          <p className="field__hint">{tx.create.memberStatusNote}</p>
        </div>
      )}
    </Sheet>
  );
}

export function EditDamageSheet({ damage, role, onClose }: { damage: Damage; role: MemberRole; onClose: () => void }) {
  const ref = useDamageRef();
  const write = useWrite();
  const isEditor = hasAtLeastRole(role, 'editor');
  const [categories, setCategories] = useState<DamageCategory[]>(damage.categories);
  const [title, setTitle] = useState(damage.title);
  const [description, setDescription] = useState(damage.description ?? '');
  const [status, setStatus] = useState<DamageStatus>(damage.status);
  const [saving, setSaving] = useState(false);
  // Status chips offer only what the transition table allows (verworfen needs a reason, so it stays a review action).
  // Only what a plain edit may set: reject, merge and accept need a reason and go through the review (statusPatchAllowed).
  const reachable = [damage.status, ...allowedActions(damage.status).filter((a) => !actionNeedsReason(a)).map(targetStatus)].filter((s, i, all) => s !== 'verworfen' && all.indexOf(s) === i);

  const save = async () => {
    if (validateDamageTitle(title) || (isEditor && !categories.length)) return;
    const before: UpdateDamageInput = { title: damage.title, description: damage.description, categories: damage.categories, status: damage.status };
    const patch: UpdateDamageInput = isEditor ? { title, description: description.trim() || null, categories, status } : { title, description: description.trim() || null };
    setSaving(true);
    const result = await write({
      run: (s) => s.updateDamage(damage.id, patch, { expectedUpdatedAt: damage.updatedAt }),
      success: tx.toast.updated,
      undo: (s, r) => s.updateDamage(damage.id, isEditor ? before : { title: before.title, description: before.description }, { undoOf: r.eventId }),
    });
    setSaving(false);
    if (result) onClose();
  };

  return (
    <Sheet
      title={tx.create.editTitle}
      kicker={<span className="mono label">{ref(damage.number)}</span>}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {tx.app.cancel}
          </button>
          <button type="button" className="btn btn--primary" disabled={saving} onClick={save}>
            <Icon name="check" />
            {tx.app.save}
          </button>
        </>
      }
    >
      {isEditor && (
        <MultiChoiceChips
          label={de.damage.category}
          values={categories}
          options={categoryOptions()}
          onChange={setCategories}
          hint={tx.create.categoriesHint}
          error={categories.length ? null : tx.create.categoryRequired}
        />
      )}
      <TextFields title={title} description={description} onTitle={setTitle} onDescription={setDescription} showErrors />
      {isEditor && <ChoiceChips label={de.damage.status} value={status} options={statusOptions(reachable)} onChange={setStatus} />}
    </Sheet>
  );
}
